import { useState, type ReactNode } from "react";

import {
    BaseItem as ZhBaseItem,
    Button as ZhButton,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

export type TradeManageRecord = Record<string, unknown>;

export interface TradeManageMigrationProps {
    title: string;
    data?: TradeManageRecord;
    items?: TradeManageRecord[];
    fields?: string[];
    dialog?: boolean;
    open?: boolean;
    children?: ReactNode;
    onClose?: () => void;
    onSubmit?: (data: TradeManageRecord) => void;
    onSelect?: (item: TradeManageRecord) => void;
    onAction?: (item: TradeManageRecord) => void;
}

/** 深层业务组件的真实数据承载器：展示字段、列表和操作回调，供迁移阶段保持旧入口可组合。 */
export function TradeManageMigrationPanel({
    title,
    data = {},
    items = [],
    fields = ["id", "name", "state", "createTime"],
    dialog = false,
    open = false,
    children,
    onClose,
    onSubmit,
    onSelect,
    onAction
}: TradeManageMigrationProps) {
    const [draft, setDraft] = useState<TradeManageRecord>(data);
    // 深层迁移组件统一通过 zhihao-ui-react Table 渲染，避免回退为原生 table。
    const columns: TableColumnProps<TradeManageRecord>[] = fields.map(field => ({
        key: field,
        label: field,
        prop: field,
        render: ({ row }) => String(row[field] ?? "--")
    }));
    const content = (
        <div>
            <ZhGrid cols={4} gap={[12, 16]}>
                {fields.map(field => (
                    <ZhBaseItem key={field} label={field} value={String(draft[field] ?? data[field] ?? "--")} />
                ))}
            </ZhGrid>
            {items.length ? (
                <div className="mt-4 overflow-auto">
                    <ZhTable<TradeManageRecord>
                        columns={columns}
                        data={items}
                        pagination={false}
                        rowKey={row => String(row.id ?? items.indexOf(row))}
                        onRowClick={row => onSelect?.(row)}
                    />
                </div>
            ) : null}
            <label className="mt-4 block">
                <span className="mr-2">备注</span>
                <input
                    className="rounded border p-2"
                    value={String(draft.remark ?? "")}
                    onChange={event => setDraft({ ...draft, remark: event.target.value })}
                />
            </label>
            {children}
            <div className="mt-4 flex justify-end gap-2">
                {onAction ? <ZhButton onClick={() => onAction(data)}>处理</ZhButton> : null}
                {onClose && !dialog ? <ZhButton onClick={onClose}>取消</ZhButton> : null}
                {onSubmit && !dialog ? (
                    <ZhButton type="primary" onClick={() => onSubmit({ ...data, ...draft })}>
                        保存
                    </ZhButton>
                ) : null}
            </div>
        </div>
    );
    if (!dialog)
        return (
            <section className="section-card mb-4">
                <div className="section-title mb-4">{title}</div>
                {content}
            </section>
        );
    return (
        <ZhDialog
            open={open}
            title={title}
            width={640}
            onCancel={onClose}
            footer={
                <div className="flex justify-end gap-2">
                    <ZhButton onClick={onClose}>取消</ZhButton>
                    {onSubmit ? (
                        <ZhButton type="primary" onClick={() => onSubmit({ ...data, ...draft })}>
                            确认
                        </ZhButton>
                    ) : null}
                </div>
            }
        >
            {content}
        </ZhDialog>
    );
}
